import dataModel from '../../models/DataModel.js';
import projectsService from '../../services/projectsService';
import AOCollection from './AOCollection';
import ReduxConstants from '../../constants/ReduxConstants.js';
import { RecordStates } from '../../constants/ReduxConstants';
import { nextPageForLoadedItems } from '../../utils/infiniteScrollUtils';

export default class ProjectsCollection extends AOCollection {
  constructor(name, perPage, searchString, userId, companyId, subKey, filter, recordDescriptor, markets, include_favorites) {
    super({
      name: name,
      collectionType: ReduxConstants.PROJECT,
      perPage, 
      searchString, 
      userId,
      companyId, 
      subKey,
      filter,
      recordDescriptor,
    });
    this._markets = markets;
    this._include_favorites = include_favorites;
    this.processItemActionCable = this.processItemActionCable.bind(this);
  }

  static build(name, perPage, searchString, userId, companyId, subKey, filter, recordDescriptor, getAllProjects = false, markets, include_favorites) {
    let collection = new ProjectsCollection(name, perPage, searchString, userId, companyId, subKey, filter, recordDescriptor, markets, include_favorites);
    if (perPage) {
      collection.page = 1;
    }
    collection.getAllProjects = getAllProjects;
    let collectionData = dataModel.collectionsDAO.findCollection(collection.getKey());
    if (!collectionData) {
      collectionData = collection.getDefaultData();
      collectionData = dataModel.collectionsDAO.setCollection(collection.getKey(), collectionData, collection.getCollectionType());
    }
    else {
      collection._filter = collectionData._filter;
    }
    return collection;
  }

  handleOffLineFetch(options) {

    let denormalizedCollection = this.getDenormalizedCollection(options);
    return new Promise((resolve) => {
      let reg = null;
      if (this._searchString) {
        const words = this._searchString.toLowerCase().split(/\s/);
        let exp = '^(?=.*\\b' + words[0] + '\\S*)';
        if (words.length > 0) {
          words.forEach((word, index) => {
            if (index > 0) {
              exp += '(?=.*\\b' + word + '\\S*)';
            }
          });
        }
        exp += '.+';
        reg = new RegExp(exp);
      }
      denormalizedCollection.ids = [];
      const foundProjects = denormalizedCollection.projects.filter(async project => {
        if (reg) {
          if (reg.test(project.name.toLowerCase())) {
            denormalizedCollection.ids.push(project.id);
            return true;
          }
        }
        else {
          return true;
        }
        return false;
      });

      denormalizedCollection.projects = foundProjects;
      resolve(denormalizedCollection);
    });
  }

  get projects() {
    let collection = this.findCollection(this.getKey());
    return collection.items;
  }

  async fetchNext(pageOverride, options = {}) {
    
    let marketIds = null;
    if (this._markets) {
      marketIds = this._markets.map(market => { return market.id; });
    }
    else {
      marketIds = await dataModel.usersDAO.getEditorUserMarkets(this.userId ? this.userId : dataModel.usersDAO.currentUser.id);
    }
    options.marketIds = marketIds;

    if (!options.recordDescriptor) {
      options.recordDescriptor = this.recordDescriptor;
    }
    if (dataModel.isOffline) {
      return this.handleOffLineFetch(options);
    }
    else {
      return new Promise(async (resolve) => {
        if (pageOverride) {
          this.page = pageOverride;
        }
        else {
          // Derive the next page from what is actually loaded rather than incrementing a counter:
          // after a reload or a tab switch fetchCurrent has restored several pages of rows in one
          // page-1 request, and a plain this.page++ would re-request rows already on screen.
          // See nextPageForLoadedItems for why that wedges the infinite scroll.
          const collection = this.findCollection(this.getKey());
          const loaded = collection && collection.ids ? collection.ids.length : 0;
          this.page = nextPageForLoadedItems(loaded, this.perPage || (collection && collection.perPage), this.page);
        }

        //if you want favorites, set noFavorites parameter to false
        const noFavorites = options.getFavorites || this._include_favorites ? false : true;

        let serviceCall;
        if (this.getAllProjects) {
          // Projects Tab
          // search() is (…, includeArchived, ungrouped, options). `options` was being passed in the
          // `ungrouped` slot, leaving `options` undefined: the request went out with no `filtering`
          // (so no recordDescriptor -> no included resources, and every row then fetched its own
          // project and project template) and a junk `ungrouped=[object Object]`. Pass ungrouped
          // explicitly as null - it was never actually enabled, since the server compares it to 'true'.
          serviceCall = projectsService.search.bind(this, this._searchString, this._filter, this._filter && this._filter.showArchived,
            null, null, this.page, this.perPage, null, noFavorites, false, null, options);
        }
        else {
          // Tasks Tab
          serviceCall = projectsService.searchMyProjects.bind(this, this._searchString, this.sortField, this.ascending,
            this._filter, this.page, this.perPage, true, options);
        }
      
        let newCollection = await this._fetchData(serviceCall, options, !pageOverride);
        if (newCollection.offline) {
          return this.handleOffLineFetch(options);
        }

        newCollection.totalProjects = newCollection.meta ? newCollection.meta.total_projects : 0;
        return resolve(newCollection);
      });
    }
  }

  async fetchCurrent(shouldPaginate = true, options = {}, prefetchCallback) {
    let marketIds = null;
    if (this._markets) {
      marketIds = this._markets.map(market => { return market.id; });
    }
    else {
      marketIds = await dataModel.usersDAO.getEditorUserMarkets(this.userId ? this.userId : dataModel.usersDAO.currentUser.id);
    }

    options.marketIds = marketIds;

    if (!options.recordDescriptor) {
      options.recordDescriptor = this.recordDescriptor;
    }
    if (dataModel.isOffline) {
      return this.handleOffLineFetch(options);
    }
    else {
      return new Promise(async (resolve) => {
        let collection = this.findCollection(this.getKey());

        let page = null;
        let perPage = null;
        
        // Get everything we already got
        if (shouldPaginate) {
          page = 1;
          if (collection && collection.perPage) {
            perPage = Math.max(this.perPage, collection.ids.length);
          }
          else {
            perPage = this.perPage;
          }
        }

        //if you want favorites, set noFavorites parameter to false
        const noFavorites = options.getFavorites || this._include_favorites ? false : true;

        let serviceCall;
        if (this.getAllProjects) {
          // Projects Tab
          // see fetchNext: `options` must be the 12th arg, after `ungrouped`
          serviceCall = projectsService.search.bind(this, this._searchString, this._filter, this.filter && this.filter.showArchived,
            null, null, page, perPage, null, noFavorites, this.filter && this.filter.includeArchived, null, options);
        }
        else {
          // Tasks Tab
          serviceCall = projectsService.searchMyProjects.bind(this, this._searchString, this.sortField, this.ascending, this._filter,
            page, perPage, true, options); // Don't get favorits
        }
      
        let newCollection = await this._fetchData(serviceCall, options, false, null, prefetchCallback);
        if (newCollection.offline) {
          return this.handleOffLineFetch(options);
        }
    
        newCollection.totalProjects = newCollection.meta ? newCollection.meta.total_projects : 0;
        return resolve(newCollection);
      });
    }
  }

  async processItemActionCable(updatedProject, acData, recordDescriptor) {
    return new Promise((resolve) => {
      const projectsCollection = this.getDenormalizedCollection({ recordDescriptor });
      if (acData.type == 'newProject') {
        if (updatedProject) {
          if (this.getAllProjects){
            projectsCollection.projects.push(updatedProject);
            projectsCollection.projects.sort((p1, p2) => {
              return p1.name < p2.name ? -1 : p1.name > p2.name ? 1 : 0;
            });
            projectsCollection.meta.total_projects++;
            projectsCollection.totalRecords = projectsCollection.totalProjects = projectsCollection.meta.total_projects;
            projectsCollection.ids = this.normalizeCollection(projectsCollection.projects, { store: true });
            dataModel.collectionsDAO.setCollection(this.getKey(), projectsCollection, this.getCollectionType());
          }
          else {
            this.markNeedsSync(projectsCollection);
          }
          return resolve(this.getDenormalizedCollection({ recordDescriptor }));
        }
      }
      else if (acData.type == 'updateProject') {
        if (updatedProject) {
          let found = false;
          
          projectsCollection.projects = projectsCollection.projects.map(proj => {
            if (proj.id == updatedProject.id) {
              found = true;
              return updatedProject;
            }
            else {
              return proj;
            }
          });
          if (!found) {
            let addProject = false;
            if (this.getAllProjects) {
              addProject = true;
            }
            else {
              if (updatedProject.project_users &&
                updatedProject.project_users.filter(user => { return user.user_id == dataModel.usersDAO.currentUser.id;}).length > 0) {
                addProject = true;
              }
              else if (updatedProject.users &&
                updatedProject.users.filter(user => { return user.id == dataModel.usersDAO.currentUser.id;}).length > 0) {
                addProject = true;
              }
            }
            if (addProject) {
              projectsCollection.projects.push(updatedProject);
              projectsCollection.meta.total_projects++;
              projectsCollection.totalRecords = projectsCollection.totalProjects = projectsCollection.meta.total_projects;
            }
          }
          projectsCollection.ids = this.normalizeCollection(projectsCollection.projects, { store: true });
          dataModel.collectionsDAO.setCollection(this.getKey(), projectsCollection, this.getCollectionType());
          return resolve(this.getDenormalizedCollection({ recordDescriptor }));
        }
      }
      else if (acData.type == 'deleteProject') {
        let found = false;
        projectsCollection.projects = projectsCollection.projects.filter(proj => {
          if (proj.id != acData.project_id){
            return true;
          }
          else {
            found = true;
            return false;
          }
        });
        if (found) {
          projectsCollection.meta.total_projects--;
        }
        projectsCollection.totalRecords = projectsCollection.totalProjects = projectsCollection.meta.total_projects;
        projectsCollection.ids = this.normalizeCollection(projectsCollection.projects, { store: true });
        dataModel.collectionsDAO.setCollection(this.getKey(), projectsCollection, this.getCollectionType());
        return resolve(this.getDenormalizedCollection({ recordDescriptor }));
      }
    });
  }

  setFilter(filter = {}, ignoreIdReset) {
    if (this.isEmpty(filter) && this.isEmpty(this._filter)) {
      return;
    }
    if (filter != this._filter || !filter) {
      this._filter = filter ? filter : {};
      this._recordState = RecordStates.NEEDS_SYNC;
      this.ids = ignoreIdReset ? this.ids : [];
      this.page = 1;
      dataModel.collectionsDAO.setCollection(this.getKey(), this.getData(), this.getCollectionType());
    }
  }
}



// WEBPACK FOOTER //
// ./src/dao/collections/ProjectsCollection.js